import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ChevronDown, LogOut, LayoutDashboard, MessageSquare, User, FileSignature, ShieldAlert, Sun, Moon } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const dashboardPath =
    user?.role === "admin" ? "/admin" : user?.role === "employee" ? "/employee" : "/customer";

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F8F9FE]/75 border-b border-[#E4E7F5]/60">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-md bg-[#1E3A8A] flex items-center justify-center text-[#F8F9FE] font-display font-bold text-sm">
            KSB
          </div>
          <span className="font-display font-bold text-lg tracking-tight hidden sm:block">
            Keritex Staffing Bureau
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          <Link to="/browse" data-testid="nav-browse" className="hover:text-[#1E3A8A] transition-colors">
            Browse Staff
          </Link>
          <Link to="/pricing" data-testid="nav-pricing" className="hover:text-[#1E3A8A] transition-colors">
            Pricing
          </Link>
          <Link to="/#how" data-testid="nav-how" className="hover:text-[#1E3A8A] transition-colors">
            How it works
          </Link>
          {user?.role === "customer" && (
            <Link to="/messages" data-testid="nav-messages" className="hover:text-[#1E3A8A] transition-colors">
              Messages
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            aria-label="Toggle dark mode"
            className="w-9 h-9 rounded-md border border-[#E4E7F5] flex items-center justify-center hover:bg-[#EEF0FA] transition-colors"
            title={theme === "dark" ? "Switch to light" : "Switch to dark"}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="nav-user-menu"
                  variant="outline"
                  className="border-[#E4E7F5] hover:bg-[#EEF0FA] gap-2"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{user.full_name?.split(" ")[0]}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-display font-semibold">{user.full_name}</div>
                  <div className="text-xs text-[#5B6478] capitalize">{user.role} · {user.tier}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav(dashboardPath)}>
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-theme" onClick={toggle}>
                  {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                  {theme === "dark" ? "Light mode" : "Dark mode"}
                </DropdownMenuItem>
                {(user.role === "customer" || user.role === "employee") && (
                  <>
                    <DropdownMenuItem data-testid="menu-contracts" onClick={() => nav("/contracts")}>
                      <FileSignature className="w-4 h-4 mr-2" /> Contracts
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="menu-reports" onClick={() => nav("/reports")}>
                      <ShieldAlert className="w-4 h-4 mr-2" /> Reports
                    </DropdownMenuItem>
                  </>
                )}
                {user.role === "customer" && (
                  <DropdownMenuItem data-testid="menu-messages" onClick={() => nav("/messages")}>
                    <MessageSquare className="w-4 h-4 mr-2" /> Messages
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={() => {
                    logout();
                    nav("/");
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                data-testid="nav-signin-btn"
                variant="ghost"
                className="hover:bg-[#EEF0FA]"
                onClick={() => nav("/login")}
              >
                Sign in
              </Button>
              <Button
                data-testid="nav-getstarted-btn"
                className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE] rounded-md"
                onClick={() => nav("/signup")}
              >
                Get Started
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
